import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HTTP_STATUS_CODE } from '@core/common/http-status-code';
import { IS_PUBLIC_API, TOKEN_REFRESHED } from '@core/models/api-option.interface';
import { LocalStorageService } from '@core/services/local-storage.service';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AuthLoopGuardService } from './services/auth-loop-guard.service';
import { TokenRefreshService } from './services/token-refresh.service';

/**
 * Ported from the agent portal, replacing HttpRefreshTokenInterceptor: refreshes proactively when
 * the token is near expiry, and reactively on a 401 — in both cases by asking the Id server's
 * session, never with a locally held refresh token.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly tokenRefreshService = inject(TokenRefreshService);
    private readonly authLoopGuard = inject(AuthLoopGuardService);

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (request.headers.get('ILI') !== 'true') {
            return next.handle(request);
        }

        // Sync the Authorization header with the latest in-memory token. Requests can be built with
        // a stale token and arrive here after a proactive refresh completed; without this they send
        // the old token, 401, and trigger a needless second refresh cycle. A plain clone on purpose
        // — TokenRefreshService.updateToken is not used here because marking TOKEN_REFRESHED would
        // suppress the reactive 401 handler on the first attempt.
        const latestToken = this.tokenRefreshService.currentToken;
        if (latestToken) {
            request = request.clone({ setHeaders: { Authorization: `Bearer ${latestToken}` } });
        }

        const handle$ = this.tokenRefreshService.isReadyToRefreshProactively(request)
            ? this.tokenRefreshService.doRefresh(request, next)
            : next.handle(request);

        return handle$.pipe(
            // The first successful authenticated response ends any loop sequence being counted.
            tap((event) => {
                if (event instanceof HttpResponse) {
                    this.authLoopGuard.reset();
                }
            }),
            catchError((error: any | HttpErrorResponse) => {
                if (error instanceof HttpErrorResponse && error.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
                    const isPublic = request.context.get(IS_PUBLIC_API);
                    const alreadyRetried = request.context.get(TOKEN_REFRESHED);
                    const ignored = this.tokenRefreshService.isIgnoredRequest(request);

                    if (!isPublic && !ignored && !alreadyRetried) {
                        return this.tokenRefreshService.handle401(request, next, error);
                    }

                    // A freshly refreshed token still 401s — the session is accepted by Id but
                    // rejected by the backend (permission/BU desync). Left alone, soft logout →
                    // silent SSO re-login → same 401 repeats forever; once the counter says this
                    // is that loop, end the Id session itself so it cannot be re-issued.
                    if (alreadyRetried && this.authLoopGuard.registerAuthed401(LocalStorageService.getAccessToken())) {
                        this.authLoopGuard.forceHardReset('validation');
                    }
                }

                throw error;
            }),
        );
    }
}
